import React from "react";
import { Box, Chip, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import RadioGroupField from "../radioGroupField/RadioGroupField";
import { useFormikContext } from "formik";
import { PAYMENT_TYPE } from "../constants";
import { toFixedAmount } from "../toFixedAmount";
import { headerSectionStyle, infoSectionStyle } from "./tailwindClasses";
import { useFormattedAmount } from "../../hooks/useFormattedAmount";
import i18n from "../../i18n";
import { useIsGeorgianLocale } from "../../hooks/useIsGeorgianLocale";

/**
 * PriceItem component displays a title and price in a flexible box layout.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the price item
 * @param {number} props.price - The price value
 * @param {string} [props.boxStyle] - Additional styles for the Box component
 * @returns {JSX.Element} The rendered component
 */
const PriceItem = ({ title, price, boxStyle = "", typographyStyle = "" }) => {
  return (
    <Box
      className={`flex items-start sm:items-center justify-between smMax:py-2  w-full ${boxStyle}`}
    >
      <Typography variant={"subtitle1"} className={"text-gray-500 smMax:font-bold"}>
        {title}:
      </Typography>
      <Typography variant={"subtitle1"} className={`sm:p-2 whitespace-nowrap ${typographyStyle}`}>
        {price}
      </Typography>
    </Box>
  );
};

/**
 * Component for displaying payment information on small screens.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child elements to be rendered within the component.
 * @returns {JSX.Element} The rendered component.
 */
const SmallScreenPaymentByItem = ({ children }) => {
  const { t } = useTranslation();
  return (
    <Box className={"flex xl:hidden justify-between sm:items-center"}>
      <Typography variant={"subtitle1"} className={"text-gray-500 py-2"}>
        {t("orderCreation.paymentBy")}
      </Typography>
      {children}
    </Box>
  );
};

const PricesInfo = () => {
  const {
    values: {
      paymentType,
      insuranceCost,
      codCommissionAmount,
      codCommissionAmountCash,
      deliveryCost,
      estimatedCost,
      cod,
      codCommissionByRecipient,
      codIncludesOperationalCosts,
      returnParcelToSender,
      returnParcelToSenderCost,
      returnParcelToSenderPaymentByRecipient,
    },
    handleChange,
  } = useFormikContext();

  const { t } = useTranslation();
  const isGeorgian = useIsGeorgianLocale()

  const codSum = cod ? estimatedCost : 0;
  const deliveryCostFieldValue = useFormattedAmount(deliveryCost, t);
  const insuranceFieldValue = useFormattedAmount(insuranceCost, t);
  const returnParcelToSenderValue = useFormattedAmount(returnParcelToSenderCost, t);
  const codFieldValue = useFormattedAmount(codSum, t);
  const codCommissionCashFieldValue = useFormattedAmount(codCommissionAmountCash, t);
  const codCommissionFieldValue = (codCommissionAmountCash !== codCommissionAmount) && codCommissionAmountCash
    ? `${toFixedAmount(codCommissionAmount)} ${t("orderTracking.gel")}\r\n(${codCommissionCashFieldValue} ${t("orderTracking.inCash").toUpperCase()})`
    : `${toFixedAmount(codCommissionAmount)} ${t("orderTracking.gel")}`;

  const isCodPaymentTypeRecipient = codCommissionByRecipient === PAYMENT_TYPE.RECIPIENT;
  const isPaymentTypeSender = paymentType === PAYMENT_TYPE.SENDER;
  const isReturnParcelToSenderPaymentByRecipient =
    returnParcelToSenderPaymentByRecipient === PAYMENT_TYPE.RECIPIENT;

  const calculateSumPaidBySender = () => {
    if (
      !isPaymentTypeSender &&
      isCodPaymentTypeRecipient &&
      isReturnParcelToSenderPaymentByRecipient
    ) {
      return {
        byCard: 0,
        inCash: 0,
      }
    }

    let total = 0;
    if (isPaymentTypeSender) {
      total += deliveryCost + insuranceCost;
    }
    if (!isReturnParcelToSenderPaymentByRecipient) {
      total += returnParcelToSenderCost;
    }

    if (!isCodPaymentTypeRecipient) {
      return {
        byCard: total + codCommissionAmount,
        inCash: total + codCommissionAmountCash,
      }
    }

    return {
      byCard: total,
      inCash: total,
    }
  };

  const calculateSumPaidByRecipient = () => {
    const returnParcelToSenderRecipientCost = isReturnParcelToSenderPaymentByRecipient
      ? returnParcelToSenderCost
      : 0;
    const codAmountResult = isCodPaymentTypeRecipient ? codCommissionAmount : 0;
    const codAmountResultCash = isCodPaymentTypeRecipient ? codCommissionAmountCash : 0;
    if (!isPaymentTypeSender) {
      return {
        byCard: codSum + codAmountResult + deliveryCost + insuranceCost + returnParcelToSenderRecipientCost,
        inCash: codSum + codAmountResultCash + deliveryCost + insuranceCost + returnParcelToSenderRecipientCost,
      }
    } else if (isCodPaymentTypeRecipient) {
      return {
        byCard: codSum + codCommissionAmount + returnParcelToSenderRecipientCost,
        inCash: codSum + codCommissionAmountCash + returnParcelToSenderRecipientCost,
      }
    }
    return {
      byCard: codSum + returnParcelToSenderRecipientCost,
      inCash: codSum + returnParcelToSenderRecipientCost,
    }
  };

  const sumPaidBySender = calculateSumPaidBySender();
  const sumPaidBySenderByCard = toFixedAmount(sumPaidBySender.byCard)
  const sumPaidBySenderInCash = sumPaidBySender.byCard !== sumPaidBySender.inCash ? toFixedAmount(sumPaidBySender.inCash) : null
  const sumPaidBySenderPriceItemValueByCard = `${sumPaidBySenderByCard} ${t("orderTracking.gel")}`
  const sumPaidBySenderPriceItemValueInCash = `${sumPaidBySenderInCash} ${t("orderTracking.gel")}`
  const sumPaidBySenderPriceItem = sumPaidBySenderInCash
    ? `${sumPaidBySenderPriceItemValueByCard}\r\n(${sumPaidBySenderPriceItemValueInCash} ${t("orderTracking.inCash").toUpperCase()})`
    : sumPaidBySenderPriceItemValueByCard

  const sumPaidByRecipient = calculateSumPaidByRecipient();
  const sumPaidByRecipientByCard = toFixedAmount(sumPaidByRecipient.byCard)
  const sumPaidByRecipientInCash = sumPaidByRecipient.byCard !== sumPaidByRecipient.inCash ? toFixedAmount(sumPaidByRecipient.inCash) : null
  const sumPaidByRecipientPriceItemValueByCard = `${sumPaidByRecipientByCard} ${t("orderTracking.gel")}`
  const sumPaidByRecipientPriceItemValueInCash = `${sumPaidByRecipientInCash} ${t("orderTracking.gel")}`
  const sumPaidByRecipientPriceItem = sumPaidByRecipientInCash
    ? `${sumPaidByRecipientPriceItemValueByCard}\r\n(${sumPaidByRecipientPriceItemValueInCash} ${t("orderTracking.inCash").toUpperCase()})`
    : sumPaidByRecipientPriceItemValueByCard

  const paymentTypeLabels = [
    { value: PAYMENT_TYPE.SENDER, label: t("orderCreation.paymentSender") },
    { value: PAYMENT_TYPE.RECIPIENT, label: t("orderCreation.paymentRecipient") },
  ];

  const paymentCodCommissionLabels = [
    { value: PAYMENT_TYPE.SENDER, label: t("orderCreation.paymentSender") },
    { value: PAYMENT_TYPE.RECIPIENT, label: t("orderCreation.paymentRecipient") },
  ];

  const paymentReturnOnDeliveryLabels = [
    { value: PAYMENT_TYPE.SENDER, label: t("orderCreation.paymentSender") },
    { value: PAYMENT_TYPE.RECIPIENT, label: t("orderCreation.paymentRecipient") },
  ];

  return (
    <section className={infoSectionStyle}>
      <Typography variant={"h6"} className={headerSectionStyle}>
        {t("orderCreation.stepPayment")}
      </Typography>
      <Divider orientation={"horizontal"} flexItem />
      <Box className={"flex flex-col items-start w-11/12 sm:w-5/6 p-1.5 my-2 mx-2 sm:mx-6"}>
        <PriceItem
          title={t("orderTracking.paidBySender")}
          price={sumPaidBySenderPriceItem}
          boxStyle={isGeorgian ? "xl:w-3/4" : "xl:w-1/2 xxl:w-1/3"}
          typographyStyle={isGeorgian ? "whitespace-break-spaces text-right" : ""}
        />
        <PriceItem
          title={t("orderTracking.paidByRecipient")}
          price={sumPaidByRecipientPriceItem}
          boxStyle={isGeorgian ? "xl:w-3/4" : "xl:w-1/2 xxl:w-1/3"}
          typographyStyle={isGeorgian ? "whitespace-break-spaces text-right" : "whitespace-break-spaces sm:whitespace-nowrap"}
        />
      </Box>
      <Divider orientation={"horizontal"} className="hidden md:block" flexItem />
      <Box
        className={
          "flex flex-col md:flex-row items-start w-11/12 sm:w-5/6 p-1.5 my-2 mx-2 sm:mx-6"
        }
      >
        <Box className={"flex flex-col justify-between w-full xl:w-1/2 xxl:w-1/3"}>
          <Typography variant={"h6"}>{t("orderCreation.detailsCosts")}</Typography>
          <PriceItem title={t("orderTracking.deliveryCost")} price={deliveryCostFieldValue} />
          <SmallScreenPaymentByItem>
            <RadioGroupField
              name="paymentType"
              value={paymentType}
              onChange={handleChange}
              disabled={codIncludesOperationalCosts}
              labels={paymentTypeLabels}
              className={
                "flex flex-col sm:flex-row xl:items-center sm:flex-nowrap w-full sm:py-1"
              }
            />
          </SmallScreenPaymentByItem>
          <PriceItem title={t("ordersTableHeaders.cod")} price={codFieldValue} />
          <SmallScreenPaymentByItem>
            <Chip className={"my-1 md:my-0 py-2"} label={t("ordersTableHeaders.recipient")} />
          </SmallScreenPaymentByItem>
          <PriceItem
            title={t("orderTracking.codCommission")}
            price={codCommissionFieldValue}
            typographyStyle={isGeorgian ? "whitespace-break-spaces text-right" : "whitespace-break-spaces sm:whitespace-nowrap"}
          />
          <SmallScreenPaymentByItem>
            <RadioGroupField
              name="codCommissionByRecipient"
              value={codCommissionByRecipient}
              onChange={handleChange}
              disabled={codIncludesOperationalCosts}
              labels={paymentCodCommissionLabels}
              className={
                "flex flex-col sm:flex-row sm:items-center sm:flex-nowrap w-full sm:py-2"
              }
            />
          </SmallScreenPaymentByItem>
          <PriceItem title={t("orderTracking.insurance")} price={insuranceFieldValue} />
          <SmallScreenPaymentByItem>
            <Chip
              className={"my-1 md:my-0 py-2"}
              label={
                isPaymentTypeSender
                  ? t("ordersTableHeaders.sender")
                  : t("ordersTableHeaders.recipient")
              }
            />
          </SmallScreenPaymentByItem>
          {Boolean(returnParcelToSender) && (
            <>
              <PriceItem
                title={t("ordersTableHeaders.returnParcelToSender")}
                price={returnParcelToSenderValue}
              />
              <SmallScreenPaymentByItem>
                <RadioGroupField
                  name="returnParcelToSenderPaymentByRecipient"
                  value={returnParcelToSenderPaymentByRecipient}
                  onChange={handleChange}
                  labels={paymentTypeLabels}
                  className={
                    "flex flex-col sm:flex-row xl:items-center sm:flex-nowrap w-full sm:py-1"
                  }
                />
              </SmallScreenPaymentByItem>
            </>
          )}
        </Box>
        <Box className={"hidden xl:flex flex-col md:ml-12"}>
          <Typography variant={"h6"}>{t("orderCreation.paymentBy")}</Typography>
          <RadioGroupField
            name="paymentType"
            value={paymentType}
            onChange={handleChange}
            disabled={codIncludesOperationalCosts}
            labels={paymentTypeLabels}
            className={"flex flex-col md:flex-row md:items-center md:flex-nowrap w-full"}
          />
          <Chip className={"my-1 md:my-0 py-2"} label={t("orderCreation.payRecipient")} />
          <RadioGroupField
            name="codCommissionByRecipient"
            value={codCommissionByRecipient}
            onChange={handleChange}
            disabled={codIncludesOperationalCosts}
            labels={paymentCodCommissionLabels}
            className={"flex flex-col md:flex-row md:items-center md:flex-nowrap w-full md:py-2"}
          />
          <Chip
            className={"my-1 md:my-0 py-2"}
            label={
              isPaymentTypeSender ? t("orderCreation.paySender") : t("orderCreation.payRecipient")
            }
          />
          {Boolean(returnParcelToSender) && (
            <RadioGroupField
              name="returnParcelToSenderPaymentByRecipient"
              value={returnParcelToSenderPaymentByRecipient}
              onChange={handleChange}
              disabled={codIncludesOperationalCosts}
              labels={paymentReturnOnDeliveryLabels}
              className={
                "flex flex-col md:flex-row md:items-center md:flex-nowrap w-full md:py-2"
              }
            />
          )}
        </Box>
      </Box>
    </section>
  );
};

PricesInfo.label = "orderCreation.stepPayment";

PricesInfo.validationSchema = (t) => {};

export default PricesInfo;
